import InputDocumentRg from '@components/inputs/input-document-rg/input-document-rg.component'
import InputRecordEmployment from '@components/inputs/input-record-employment/input-record-employment.component'
import { Bank, Environment, InputDocumentCpf, Loader, ObjectHelper, onEnvironmentChange } from '@hub/shared'
import FormReceiveBank from '@pages/payments-data/components/form-receive-bank/form-receive-bank.component'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import './form-payment-rpa.component.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  payload: Bank | null
  initialPayload: Bank | null
  canEdit?: {
    bank: boolean
    full: boolean
    pis: boolean
  }
  hideData: boolean
  onPayloadChange?: (payload: Bank) => any
  onValidate?: (valid: boolean) => any
}

export default ({ payload, initialPayload, canEdit, hideData, onPayloadChange, onValidate }: Props) => {
  const onEnvironmentChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [t] = useTranslation('paymentsData')
  const [urlCreators, setUrlCreators] = useState<string>('')
  const objectHelper = useMemo(() => new ObjectHelper(), [])
  const [loading, setLoading] = useState(true)
  const [valid, setValid] = useState({
    recordEmployment: true,
    formReceiveBank: false,
  })
  const [state, setState] = useState<Bank | null>(null)

  useEffect(() => {
    if (!objectHelper.compareObjects(state, payload)) {
      setState({ ...payload! })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload])

  useEffect(() => {
    if (loading && state?.holderDocument !== undefined) {
      setLoading(false)
    }
  }, [state, loading])

  const payloadChange = useCallback(
    (state: Bank) => {
      setState({ ...state })

      if (onPayloadChange && typeof onPayloadChange === 'function') {
        onPayloadChange(state)
      }
    },
    [onPayloadChange]
  )

  const handleChange = useCallback(
    (fieldName: string, fieldValue: any) => {
      ;(state as any)[fieldName] = fieldValue

      payloadChange(state!)
    },
    [state, payloadChange]
  )

  useEffect(() => {
    if (onValidate && typeof onValidate === 'function') {
      if (Object.values(valid).every((value) => value === true) && state?.nationalDocument && state?.nationalDocument?.length > 0) {
        onValidate(true)
      } else {
        onValidate(false)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, valid])

  useEffect(() => {
    onEnvironmentChangeSubscription.current = onEnvironmentChange.subscribe((environments: Environment) => {
      setUrlCreators(environments?.squidProducts?.creators)
    })
    return () => onEnvironmentChangeSubscription?.current?.unsubscribe()
  }, [])

  if (loading) {
    return (
      <div className="text-center my-5" style={{ width: '100%' }}>
        <Loader size="big" />
      </div>
    )
  }

  let labelContent = (
    <div className="display-flex justify-content-space-between" style={{ width: '100%' }}>
      <p style={{ fontWeight: 500 }}>NIS/PIS/PASEP</p>
      <p>
        <i className="fa-solid fa-circle-exclamation mr-2" style={{ color: 'var(--text_color)' }}></i>
        <a target="_blank" href={`${urlCreators}/docs/o-que-e-nit-nis-e-pispasep`}>
          {t('knowMore')}
        </a>
      </p>
    </div>
  )

  return (
    <div className={'form-payment-rpa mt-5'}>
      <fieldset>
        <InputDocumentCpf
          className="input-cpf mb-3"
          name="cpf-payment-data"
          id="cpf-payment-data"
          value={state?.holderDocument}
          label={'CPF'}
          type={hideData ? 'password' : 'text'}
          externalIcon={<i className="fa-regular fa-lock"></i>}
          readOnly={true}
          disabled={false}
        />
        <InputDocumentRg
          className="mb-3"
          name="rg-payment-data"
          id="rg-payment-data"
          value={state?.nationalDocument}
          onChange={(value) => handleChange('nationalDocument', value)}
          label={'RG'}
          type={hideData ? 'password' : 'text'}
          required
          requiredOnLoad
          readOnly={hideData || !canEdit?.full}
        />
        <InputRecordEmployment
          className="mb-3"
          name="record-employment-payment-data"
          id="record-employment-payment-data"
          value={state?.recordEmployment}
          onChange={(value) => handleChange('recordEmployment', value)}
          onValidate={(value) => setValid({ ...valid, recordEmployment: value })}
          type={hideData ? 'password' : 'text'}
          label={labelContent}
          required
          requiredOnLoad
          readOnly={hideData || !canEdit?.full || !canEdit?.pis}
        />
        <FormReceiveBank
          payload={state}
          initialPayload={initialPayload}
          canEdit={canEdit?.bank || false}
          hideData={hideData}
          onPayloadChange={(value) => {
            payloadChange(value)
          }}
          onValidate={(value) => setValid({ ...valid, formReceiveBank: value })}
        />
      </fieldset>
    </div>
  )
}
